import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPortfolio, addToPortfolio, updatePortfolio, removeFromPortfolio, searchStartups } from '../../api';
import { LogoImg } from '../../components/StartupCard';
import { RiskBadge } from '../../components/ScoreGauge';

const STATUS_LABELS = {
  suivi: { label: 'En suivi', color: '#2E75B6', bg: '#E3F2FD' },
  due_diligence: { label: 'Due Diligence', color: '#E67E22', bg: '#FFF3E0' },
  investi: { label: 'Investi', color: '#27AE60', bg: '#E8F5E9' },
  sorti: { label: 'Sorti', color: '#95A5A6', bg: '#F5F5F5' },
};
const TYPE_OPTIONS = ['Seed', 'Série A', 'Série B', 'Série C+', 'Bridge', 'Convertible', 'Autre'];

export default function PortfolioPage() {
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = () => getPortfolio().then(setPortfolio).catch(console.error);
  useEffect(() => { load(); }, []);

  const handleSearch = async () => {
    if (!searchQ.trim()) return;
    const res = await searchStartups({ q: searchQ, limit: 10 });
    setSearchResults(res.data.filter(s => !portfolio.find(p => p.id === s.id)));
  };

  const handleAdd = async (startupId) => {
    await addToPortfolio({ startup_id: startupId, status: 'suivi' });
    setShowAdd(false); setSearchQ(''); setSearchResults([]);
    load();
  };

  const handleUpdate = async (startupId) => {
    await updatePortfolio(startupId, editForm);
    setEditId(null); setEditForm({});
    load();
  };

  const handleRemove = async (startupId) => {
    if (!window.confirm('Retirer cette startup de votre portefeuille ?')) return;
    await removeFromPortfolio(startupId);
    load();
  };

  const filtered = filterStatus ? portfolio.filter(p => p.portfolio_status === filterStatus) : portfolio;

  // Stats
  const totalInvesti = portfolio.filter(p => p.portfolio_status === 'investi').length;
  const totalDD = portfolio.filter(p => p.portfolio_status === 'due_diligence').length;
  const totalSuivi = portfolio.filter(p => p.portfolio_status === 'suivi').length;

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1>Mon Portefeuille</h1>
          <button onClick={() => setShowAdd(!showAdd)} className="btn btn-accent">
            {showAdd ? 'Annuler' : '+ Ajouter une startup'}
          </button>
        </div>

        {/* KPIs */}
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          <div className="stat-card">
            <div className="stat-value">{portfolio.length}</div>
            <div className="stat-label">Total portefeuille</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#27AE60' }}>{totalInvesti}</div>
            <div className="stat-label">Investies</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#E67E22' }}>{totalDD}</div>
            <div className="stat-label">Due Diligence</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#2E75B6' }}>{totalSuivi}</div>
            <div className="stat-label">En suivi</div>
          </div>
        </div>

        {/* Add startup search */}
        {showAdd && (
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginBottom: 12 }}>Ajouter une startup au portefeuille</h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                className="form-input" placeholder="Rechercher par nom..." style={{ flex: 1 }} />
              <button onClick={handleSearch} className="btn btn-primary" style={{ fontSize: 13 }}>Rechercher</button>
            </div>
            {searchResults.length > 0 && (
              <div style={{ display: 'grid', gap: 6 }}>
                {searchResults.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <LogoImg src={s.logo_url} name={s.name} size={28} />
                      <div>
                        <strong style={{ fontSize: 13 }}>{s.name}</strong>
                        <span className="badge badge-secteur" style={{ fontSize: 9, marginLeft: 8 }}>{s.categorie}</span>
                      </div>
                    </div>
                    <button onClick={() => handleAdd(s.id)} className="btn btn-accent" style={{ fontSize: 11, padding: '4px 14px' }}>
                      Ajouter
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Filter */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <button onClick={() => setFilterStatus('')}
            className={`btn ${!filterStatus ? 'btn-primary' : 'btn-outline'}`} style={{ fontSize: 12, padding: '6px 14px' }}>
            Tous ({portfolio.length})
          </button>
          {Object.entries(STATUS_LABELS).map(([k, v]) => {
            const count = portfolio.filter(p => p.portfolio_status === k).length;
            return (
              <button key={k} onClick={() => setFilterStatus(filterStatus === k ? '' : k)}
                className={`btn ${filterStatus === k ? 'btn-primary' : 'btn-outline'}`}
                style={{ fontSize: 12, padding: '6px 14px' }}>
                {v.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Portfolio list */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-light)' }}>
            {portfolio.length === 0 ? (
              <>
                <p style={{ fontSize: 18, marginBottom: 8 }}>Votre portefeuille est vide</p>
                <p>Ajoutez des startups que vous accompagnez ou dans lesquelles vous avez investi.</p>
              </>
            ) : <p>Aucune startup avec ce filtre</p>}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {filtered.map(s => {
              const st = STATUS_LABELS[s.portfolio_status] || STATUS_LABELS.suivi;
              const isEditing = editId === s.id;
              return (
                <div key={s.id} className="card" style={{ borderLeft: `4px solid ${st.color}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', cursor: s.status === 'suspendu' ? 'default' : 'pointer', flex: 1, opacity: s.status === 'suspendu' ? 0.6 : 1 }}
                      onClick={() => s.status !== 'suspendu' && navigate(`/startup/${s.id}`)}>
                      <LogoImg src={s.logo_url} name={s.name} size={40} />
                      <div>
                        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
                          {s.name}
                        </span>
                        <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                          <span className="badge" style={{ background: st.bg, color: st.color, fontSize: 10 }}>{st.label}</span>
                          {s.status === 'suspendu' && <span className="badge" style={{ background: '#FFEBEE', color: '#C62828', fontSize: 10 }}>🔒 Désactivée</span>}
                          {s.categorie && <span className="badge badge-secteur" style={{ fontSize: 10 }}>{s.categorie}</span>}
                          {s.risk_level && <RiskBadge level={s.risk_level} />}
                          {s.note_sectorielle && (
                            <span style={{ fontSize: 13, fontWeight: 700,
                              color: parseFloat(s.note_sectorielle) > 7 ? '#27AE60' : '#F39C12' }}>
                              ISI {parseFloat(s.note_sectorielle).toFixed(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {s.status === 'suspendu' ? (
                        <>
                          <span style={{ fontSize: 11, color: '#C62828', fontWeight: 600 }}>Actions indisponibles</span>
                          <button onClick={() => handleRemove(s.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E74C3C', fontSize: 16 }} title="Retirer">
                            ✕
                          </button>
                        </>
                      ) : (
                        <>
                          <Link to={`/startup/${s.id}`} className="btn btn-primary" style={{ fontSize: 11, padding: '4px 12px', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                            Voir la fiche
                          </Link>
                          <button onClick={() => { setEditId(isEditing ? null : s.id); setEditForm({ status: s.portfolio_status, montant_investi: s.montant_investi || '', date_investissement: s.date_investissement ? s.date_investissement.split('T')[0] : '', type_investissement: s.type_investissement || '', notes: s.portfolio_notes || '' }); }}
                            className="btn btn-outline" style={{ fontSize: 11, padding: '4px 10px' }}>
                            {isEditing ? 'Annuler' : 'Modifier'}
                          </button>
                          <button onClick={() => handleRemove(s.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E74C3C', fontSize: 16 }} title="Retirer">
                            ✕
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Current info summary */}
                  {!isEditing && (s.montant_investi || s.type_investissement || s.portfolio_notes) && (
                    <div style={{ marginTop: 12, display: 'flex', gap: 20, fontSize: 13, color: 'var(--text-light)', flexWrap: 'wrap' }}>
                      {s.montant_investi && <span>Montant : <strong style={{ color: 'var(--text)' }}>{s.montant_investi}</strong></span>}
                      {s.type_investissement && <span>Type : <strong style={{ color: 'var(--text)' }}>{s.type_investissement}</strong></span>}
                      {s.date_investissement && <span>Date : <strong style={{ color: 'var(--text)' }}>{new Date(s.date_investissement).toLocaleDateString('fr-FR')}</strong></span>}
                      {s.portfolio_notes && <span>Notes : {s.portfolio_notes}</span>}
                    </div>
                  )}

                  {/* Edit form */}
                  {isEditing && (
                    <div style={{ marginTop: 16, padding: 16, background: 'var(--bg-secondary)', borderRadius: 8 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div>
                          <label className="form-label">Statut</label>
                          <select value={editForm.status} onChange={e => setEditForm(f => ({...f, status: e.target.value}))} className="form-input" style={{ fontSize: 13 }}>
                            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Montant investi</label>
                          <input type="text" value={editForm.montant_investi} onChange={e => setEditForm(f => ({...f, montant_investi: e.target.value}))}
                            className="form-input" placeholder="Ex: 500 000 €" style={{ fontSize: 13 }} />
                        </div>
                        <div>
                          <label className="form-label">Type</label>
                          <select value={editForm.type_investissement} onChange={e => setEditForm(f => ({...f, type_investissement: e.target.value}))} className="form-input" style={{ fontSize: 13 }}>
                            <option value="">-</option>
                            {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Date</label>
                          <input type="date" value={editForm.date_investissement} onChange={e => setEditForm(f => ({...f, date_investissement: e.target.value}))}
                            className="form-input" style={{ fontSize: 13 }} />
                        </div>
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <label className="form-label">Notes</label>
                        <textarea value={editForm.notes} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))}
                          className="form-input" rows={2} style={{ fontSize: 13, resize: 'vertical' }} />
                      </div>
                      <button onClick={() => handleUpdate(s.id)} className="btn btn-accent" style={{ fontSize: 13 }}>
                        Enregistrer
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
